import React from "react";
import { Table as AntTable, ConfigProvider, Tag, Badge } from "antd";
import { Typography } from "../Typography";

const Table = ({
  columns = [],
  data = [],
  className = "",
  onSort,
  sortColumn = null,
  sortDirection = null,
  rowKey = "id",
  emptyMessage = "No data available",
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  selectAll = true,
  scroll,
  ...props
}) => {
  // Detect data type from sample values
  const detectDataType = (column, data) => {
    // If type is explicitly set, use it
    if (column.type) return column.type;

    const accessor = column.accessor || column.dataIndex;
    if (!accessor) return "string";

    // Find first non-null value
    let sampleValue = null;
    for (const row of data) {
      const value =
        typeof accessor === "function"
          ? accessor(row)
          : row[accessor];
      if (value !== null && value !== undefined) {
        sampleValue = value;
        break;
      }
    }

    if (sampleValue === null || sampleValue === undefined) return "string";

    // Detect type
    if (typeof sampleValue === "boolean") return "boolean";
    if (typeof sampleValue === "number") return "number";
    if (sampleValue instanceof Date) return "date";
    if (Array.isArray(sampleValue)) return "array";
    if (typeof sampleValue === "object") return "object";
    if (typeof sampleValue === "string") {
      // Check if it's a date string
      const dateRegex = /^\d{4}-\d{2}-\d{2}/;
      if (dateRegex.test(sampleValue) && !isNaN(Date.parse(sampleValue))) {
        return "date";
      }
      // Check if it's an email
      if (sampleValue.includes("@") && sampleValue.includes(".")) {
        return "email";
      }
      // Check if it's a URL
      if (sampleValue.startsWith("http://") || sampleValue.startsWith("https://")) {
        return "url";
      }
      return "string";
    }

    return "string";
  };

  // Get default renderer based on data type
  const getDefaultRenderer = (type, column) => {
    switch (type) {
      case "boolean":
        return (value) => (
          <Badge
            status={value ? "success" : "default"}
            text={value ? "Yes" : "No"}
          />
        );

      case "number":
        return (value) => {
          if (value === null || value === undefined) return "-";
          const formatted = column.numberFormat
            ? new Intl.NumberFormat(undefined, column.numberFormat).format(value)
            : value.toLocaleString();
          return <span className="text-sm text-primary-900">{formatted}</span>;
        };

      case "date":
        return (value) => {
          if (!value) return "-";
          const date = value instanceof Date ? value : new Date(value);
          if (isNaN(date.getTime())) return value;
          const format = column.dateFormat || "short";
          let formatted;
          if (format === "short") {
            formatted = date.toLocaleDateString();
          } else if (format === "long") {
            formatted = date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            });
          } else if (format === "datetime") {
            formatted = date.toLocaleString();
          } else {
            formatted = new Intl.DateTimeFormat(undefined, format).format(date);
          }
          return <span className="text-sm text-primary-900">{formatted}</span>;
        };

      case "email":
        return (value) => (
          <a
            href={`mailto:${value}`}
            className="text-sm text-brand-blue hover:underline"
          >
            {value}
          </a>
        );

      case "url":
        return (value) => (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-brand-blue hover:underline"
          >
            {value}
          </a>
        );

      case "array":
        return (value) => {
          if (!Array.isArray(value) || value.length === 0) return "-";
          return (
            <div className="flex flex-wrap gap-1">
              {value.map((item, idx) => (
                <Tag key={idx} className="text-xs">
                  {typeof item === "object" ? JSON.stringify(item) : String(item)}
                </Tag>
              ))}
            </div>
          );
        };

      case "object":
        return (value) => {
          if (!value) return "-";
          return (
            <span className="text-xs text-primary-900 font-mono">
              {JSON.stringify(value)}
            </span>
          );
        };

      case "string":
      default:
        return (value) => {
          if (value === null || value === undefined) return "-";
          return (
            <Typography as="span" className="text-sm text-primary-900">
              {String(value)}
            </Typography>
          );
        };
    }
  };

  const accessorForRow = (column, record) => {
    const accessor = column.accessor || column.dataIndex;
    if (typeof accessor === "function") return accessor(record);
    if (accessor == null || accessor === "") return undefined;
    return record[accessor];
  };

  // Convert custom column format to Ant Design format
  const antColumns = columns.map((column) => {
    const dataType = detectDataType(column, data);
    const defaultRenderer = getDefaultRenderer(dataType, column);
    const rawAccessor = column.accessor || column.dataIndex;
    const dataIndexForAntd =
      typeof rawAccessor === "function" ? undefined : rawAccessor;

    // Ant Design only resolves string/array dataIndex; function accessors need explicit resolution.
    const renderFunction = column.render
      ? (value, record, index) =>
          column.render(accessorForRow(column, record), record, index)
      : (value, record, index) =>
          defaultRenderer(accessorForRow(column, record));

    const antColumn = {
      title: column.header || column.title,
      dataIndex: dataIndexForAntd,
      key:
        column.key ||
        (typeof rawAccessor === "string" ? rawAccessor : undefined) ||
        column.header ||
        column.title,
      render: renderFunction,
      sorter:
        column.sortable === false
          ? false
          : (a, b) => {
              const aVal = column.accessor
                ? typeof column.accessor === "function"
                  ? column.accessor(a)
                  : a[column.accessor]
                : null;
              const bVal = column.accessor
                ? typeof column.accessor === "function"
                  ? column.accessor(b)
                  : b[column.accessor]
                : null;
              
              // Handle null/undefined values
              if (aVal === null || aVal === undefined) return 1;
              if (bVal === null || bVal === undefined) return -1;
              
              // Type-aware sorting
              if (dataType === "number") {
                return Number(aVal) - Number(bVal);
              }
              if (dataType === "date") {
                const aDate = aVal instanceof Date ? aVal : new Date(aVal);
                const bDate = bVal instanceof Date ? bVal : new Date(bVal);
                if (isNaN(aDate.getTime())) return 1;
                if (isNaN(bDate.getTime())) return -1;
                return aDate.getTime() - bDate.getTime();
              }
              if (dataType === "boolean") {
                return (aVal ? 1 : 0) - (bVal ? 1 : 0);
              }
              
              // String sorting
              const aStr = String(aVal).toLowerCase();
              const bStr = String(bVal).toLowerCase();
              if (aStr < bStr) return -1;
              if (aStr > bStr) return 1;
              return 0;
            },
    };

    // Add alignment for number columns
    if (dataType === "number" && !column.align) {
      antColumn.align = "right";
    }

    // Remove undefined values
    Object.keys(antColumn).forEach(
      (key) => antColumn[key] === undefined && delete antColumn[key]
    );

    return antColumn;
  });

  // Configure row selection
  const rowSelection = selectable
    ? {
        selectedRowKeys: selectedRows,
        onChange: (selectedRowKeys, selectedRows) => {
          if (onSelectionChange) {
            onSelectionChange(selectedRowKeys, selectedRows[0], true);
          }
        },
        onSelect: (record, selected) => {
          if (onSelectionChange) {
            const rowId = record[rowKey];
            let newSelection;
            if (selected) {
              newSelection = [...selectedRows, rowId];
            } else {
              newSelection = selectedRows.filter((id) => id !== rowId);
            }
            onSelectionChange(newSelection, record, selected);
          }
        },
        onSelectAll: (selected, selectedRows, changeRows) => {
          if (onSelectionChange) {
            if (selected) {
              const allIds = data.map((row) => row[rowKey]);
              onSelectionChange(allIds, null, true);
            } else {
              onSelectionChange([], null, false);
            }
          }
        },
      }
    : undefined;

  return (
    <div className={`w-full overflow-x-auto ${className}`}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "#1A365D",
            borderRadius: 8,
          },
        }}
      >
        <AntTable
          columns={antColumns}
          dataSource={data}
          rowKey={rowKey}
          rowSelection={rowSelection}
          pagination={false}
          scroll={scroll || { x: "max-content" }}
          locale={{
            emptyText: emptyMessage,
          }}
          className="ant-table-custom"
          {...props}
        />
      </ConfigProvider>
    </div>
  );
};

export default Table;

